import {
  closestCenter,
  closestCorners,
  CollisionDetection,
  pointerWithin,
  rectIntersection,
} from "@dnd-kit/core";

import { NAVBAR_DROPPABLE_AREA_ID } from "@ryde/shared";

const RIDE_ID_POSITION = 2;
const MIN_PARTS_LENGTH = 3;

const extractDraggedRideId = (
  activeId: string | number | undefined,
): number | null => {
  if (
    activeId &&
    typeof activeId === "string" &&
    activeId.startsWith("GRID_RIDE_")
  ) {
    const parts = activeId.split("_");
    if (parts.length >= MIN_PARTS_LENGTH) {
      const rideId = Number.parseInt(parts[RIDE_ID_POSITION], 10);
      if (!Number.isNaN(rideId)) {
        return rideId;
      }
    }
  }
  return null;
};

export const createRidesCollisionDetection = (): CollisionDetection => {
  return args => {
    const droppableContainers = args.droppableContainers || [];
    const disabledIds = new Set(
      droppableContainers
        .filter(container => container.disabled)
        .map(container => container.id),
    );

    const activeId = args.active?.id;
    const draggedRideId = extractDraggedRideId(activeId);

    const filterCollisions = (
      collisions: { id: string | number }[],
    ): { id: string | number }[] => {
      const navbarButton = collisions.find(
        (c: { id: string | number }) =>
          !disabledIds.has(c.id) && String(c.id) === NAVBAR_DROPPABLE_AREA_ID,
      );

      const tableRows = collisions.filter((c: { id: string | number }) => {
        const idStr = String(c.id);

        if (
          disabledIds.has(c.id) ||
          idStr.includes("_details_") ||
          !idStr.startsWith("GRID_RIDE_")
        ) {
          return false;
        }
        return true;
      });

      if (tableRows.length > 0) {
        const otherRows = tableRows.filter((c: { id: string | number }) => {
          if (draggedRideId === null) {
            return true;
          }
          const idStr = String(c.id);
          const parts = idStr.split("_");
          if (parts.length >= MIN_PARTS_LENGTH) {
            const rideId = Number.parseInt(parts[RIDE_ID_POSITION], 10);
            if (!Number.isNaN(rideId) && rideId === draggedRideId) {
              return false;
            }
          }
          return true;
        });

        if (navbarButton) {
          if (otherRows.length > 0) {
            return [navbarButton, ...otherRows];
          }
          return [navbarButton, ...tableRows];
        }

        if (otherRows.length > 0) {
          return otherRows;
        }

        return tableRows;
      }

      if (navbarButton) {
        return [navbarButton];
      }

      return collisions.filter(
        (c: { id: string | number }) =>
          !disabledIds.has(c.id) && !String(c.id).includes("_details_"),
      );
    };

    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) {
      const filtered = filterCollisions(pointerCollisions);
      if (filtered.length > 0) {
        if (filtered.length > 1) {
          const modifiedArgs = {
            ...args,
            collisions: filtered,
          };
          const closest = closestCenter(modifiedArgs);
          const filteredClosest = filterCollisions(closest);
          return filteredClosest.length > 0 ? filteredClosest : filtered;
        }

        return filtered;
      }
    }

    const rectCollisions = rectIntersection(args);
    if (rectCollisions.length > 0) {
      const filtered = filterCollisions(rectCollisions);
      if (filtered.length > 0) {
        if (filtered.length > 1) {
          const modifiedArgs = {
            ...args,
            collisions: filtered,
          };
          const closest = closestCenter(modifiedArgs);
          const filteredClosest = filterCollisions(closest);
          return filteredClosest.length > 0 ? filteredClosest : filtered;
        }
        return filtered;
      }
    }

    const closestCenterCollisions = closestCenter(args);
    const filteredClosestCenter = filterCollisions(closestCenterCollisions);
    if (filteredClosestCenter.length > 0) {
      return filteredClosestCenter;
    }

    const closestCornersCollisions = closestCorners(args);
    const filteredClosestCorners = filterCollisions(closestCornersCollisions);
    if (filteredClosestCorners.length > 0) {
      return filteredClosestCorners;
    }

    return [];
  };
};
